<template lang="html">
  <v-container
    grid-list-md
    fluid
    pa-0
    :mt-8="depth > 0 && index > 0"
  >        
    <v-layout
      align-center
      ma-0
    >
      <v-btn
        depressed
        @click="conditionType = conditionType === 'all' ? 'any' : 'all'"
      >
        <span style="width:50px">{{ conditionType }}</span>
      </v-btn>

        
      <v-divider
        vertical
        class="mx-2"
      />

      <v-flex pa-0>
        <v-flex
          v-for="(condition, i) in rules"
          :key="condition.id"
          pa-0
        >
          <SiConditionEditorCondition
            v-if="condition.rules"
            v-model="rules[i]"
            :fields="fields"
            :field-id="fieldId"
            :depth="depth + 1"
            :index="i"
            @delete-group="() => removeGroup(i)"
          />  

          <v-layout
            v-else
            :key="i"
            ma-0
            align-center
          >
            <v-flex shrink>
              <v-btn
                depressed
                @click="condition.compare = !condition.compare"
              >
                <span style="width:80px">{{ condition.compare ? 'Compare' : 'Value' }}</span>
              </v-btn>
            </v-flex>

            <v-flex xs4>
              <SiInputAutocomplete
                ref="key_input"
                v-model="condition.key"
                outlined
                single-line
                hide-details
                autofocus
                :items="fieldOptions"
                label="Field name"
                :success="ruleIsValid(i)"
                :error="!condition.key"
              />
            </v-flex>
            <v-flex xs2>
              <SiInputAutocomplete
                v-model="condition.operator"
                outlined
                single-line
                hide-details
                :items="operators"
                label="Operator"
                :success="ruleIsValid(i)"
                :error="!condition.operator"
                @change="(v) => handleOperatorChange(v, i)"
              />
            </v-flex>
            <v-flex xs4>
              <div v-if="!condition.compare && !['in', 'notIn'].includes(condition.operator) && condition.operator !== 'hasValue'">
                <v-autocomplete
                  v-if="valueOptions(condition.key)"
                  v-model="condition.value"
                  outlined
                  single-line
                  hide-details
                  :items="valueOptions(condition.key)"
                  label="Value"
                  :success="ruleIsValid(i)"
                  :error="!condition.value"
                  @keydown.tab="ruleTab($event, i, (rules || []).length)"
                >
                  <template #item="{ item }">
                    <v-list-item-content>
                      <v-list-item-title v-html="item.value" />
                      <v-list-item-subtitle v-html="item.valueText" />
                    </v-list-item-content>
                  </template>
                </v-autocomplete>
                <!-- v-model="condition.value" -->

                <SiInputText
                  v-else
                  :value="condition.value ? condition.value.toString() : condition.value"
                  outlined
                  single-line
                  hide-details
                  label="Value"
                  :success="ruleIsValid(i)"
                  :error="!condition.value"
                  @input="e => condition.value = e"
                  @keydown.tab="ruleTab($event, i, (rules || []).length)"
                />
              </div>
                      
              <SiInputAutocomplete
                v-if="condition.compare && !['in', 'notIn'].includes(condition.operator)"
                v-model="condition.compareKey"
                outlined
                single-line
                hide-details
                :items="fieldOptions"
                label="Compare field name"
                :success="ruleIsValid(i)"
                :error="!condition.compareKey"
                @keydown.tab="ruleTab($event, i, (rules || []).length)"
              />
              <v-combobox
                v-if="['in', 'notIn'].includes(condition.operator)"
                v-model="condition.value"
                outlined
                single-line
                hide-details
                label="Values"
                multiple
                chips
                :return-object="false"
                item-value="value"
                :items="valueOptions(condition.key)"
                :success="ruleIsValid(i)"
                :error="!condition.value"
                @keydown.tab="ruleTab($event, i, (rules || []).length)"
              >
                <template #item="{ item, attrs }">
                  <v-list-item-action>
                    <v-icon v-text="attrs.inputValue ? 'check_box' : 'check_box_outline_blank'" />
                  </v-list-item-action>
                  <v-list-item-content>
                    <v-list-item-title v-html="item.value" />
                    <v-list-item-subtitle v-html="item.valueText" />
                  </v-list-item-content>
                </template>
              </v-combobox>
            </v-flex>
            <v-flex shrink>
              <v-btn
                text
                icon
                color="grey"
                class="ma-0"
                tabindex="-1"
                @click="removeCondition(i)"
              >
                <v-icon>close</v-icon>
              </v-btn>
            </v-flex>
          </v-layout>
        </v-flex>
      </v-flex>
      <v-flex
        v-if="!hasChildConditions"
        shrink
        style="align-self: flex-end; margin-bottom: 14px;"
        pa-0
      >
        <v-btn
          icon
          color="grey"
          class="my-0"
          @click="addCondition"
        >
          <v-icon>add</v-icon>
        </v-btn>
      </v-flex>
    </v-layout> 
    <v-btn
      v-if="depth < 1"
      icon
      text
      class="my-0"
      style="margin-left: 72px"
      @click="addGroup"
    >
      <v-icon>add</v-icon>
    </v-btn>
  </v-container>
</template>

<script>
import { mapGetters } from 'vuex'
import helpers from '@/mixins/helpers'
import conditions from '@/mixins/conditions'
import uniqid from 'uniqid'

export default {
  mixins: [ conditions, helpers ],
  props: {
    value: [Object, Array],
    fields: Object,
    fieldId: String,
    index: Number,
    depth: {
      type: Number,
      default: 0
    }
  },
  data () {
    return {
      lazyValue: {},
      operators: [
        { text: 'Equals (=)', value: 'equals' },
        { text: 'Not Equals (/=)', value: 'notEquals' },
        { text: 'Greater Than (>)', value: 'greaterThan' },
        { text: 'Less Than (<)', value: 'lessThan' },
        { text: 'Has Value (>"")', value: 'hasValue' },
        { text: 'Is in (=>)', value: 'in' },
        { text: 'Is not in (/=>)', value: 'notIn' }
      ]
    }
  },
  computed: {
    ...mapGetters(['_calculators']),

    rules: {
      get () {
        let { lazyValue } = this
        return lazyValue.rules || []
      },
      set (val) {
        let { lazyValue } = this
        lazyValue.rules = val
      }
    },
    conditionType: {
      get () {
        let { lazyValue } = this
        return lazyValue.type
      },
      set (v) {
        this.lazyValue.type = v
      }
    },
    hasChildConditions () {
      return !!(this.rules[0] || {}).rules
    },
    fieldOptions () {
      return this._objectToArrayAplha((this.fields || {}), 'name')
        .map(x => x.name)
        .concat(this.rowVariables)
        .concat(Object.keys(this.privateFields))
    },
    variablesInRow () {
      let variables = {}
      this._objectToArrayAplha((this.fields || {}), 'name')
        .filter(f => f.inputType === 'SiMultiItemInput')
        .forEach(f => {
          f.fields.forEach(childField => {
            variables[childField] = f.id
          })
        })
      return variables
    },
    rowVariables () {
      let multiLineId = this.variablesInRow[this.fieldId]

      if (!multiLineId) return []

      if (!this.fields[multiLineId]) return []

      return this.fields[multiLineId].fields.map(x => `row:${x}`)
    },
    privateFields ()  {
      let { variants = {} } = this._calculators[this.$route.params.sub] || {}
      return {
        _variant: { id: '_variant', name: '_variant', items: Object.keys(variants).map(k => ({ value: k, text: variants[k].name }))}
      }
    }
  },
  watch: {
    lazyValue: {
      handler (v) {
        if (v.rules) {
          v.rules.forEach(rule => {
            if (rule.value === 'true') rule.value = true
            if (rule.value === 'false') rule.value = false
          })
        } else {
          v.rules = []
        }
        this.$emit('input', v)
      },
      deep: true,
    },
    value : {
      handler (v) {
        this.lazyValue = v
      },
      deep: true,
      immediate: true
    }
  },
  methods: {
    newconditionTemplate () {
      return { id: uniqid(), compare: false, operator: 'equals' }
    },
    ruleIsValid () {
      return true
    },
    valueOptions (fieldId) {
      let { items } = { ...this.fields, ...this.privateFields }[fieldId] || {}
      return items ? items.map(i => ({ value: i.value, text: i.value, valueText: i.text })) : null
    },
    ruleTab (e, i, listLength) {
      if (e.shiftKey) return
      if (i < listLength - 1) return
      this.rules.push(this.newRuleTemplate())
    },
    removeCondition (i) {
      this.rules.splice(i, 1)
      if (this.rules.length === 0) {
        this.$emit('delete-group', this.depth)
      }
    },
    addCondition () {
      this.rules = [...this.rules, this.newconditionTemplate()]
    },
    handleEnter (e) {
      if (e.ctrlKey) this.$emit('save')
    },
    handleOperatorChange (operator, index) {
      let condition = this.rules[index]
      let { value } = condition

      if (['in', 'notIn'].includes(operator)) {
        if (typeof value !== 'object') value = value ? [value] : []
      } else if (!['in', 'notIn'].includes(operator) && typeof value === 'object' ) {
          value = (value || []).length === 1 ? value[0] : undefined
      } else if (['hasValue'].includes(operator)) {
        value = undefined
      }
      this.$set(this.rules, index, { ...condition, value })
    },
    addGroup () {
      let { hasChildConditions, newconditionTemplate } = this
      let newCondition = { id: uniqid(), type: 'all', rules: [newconditionTemplate()]}

      if (hasChildConditions) {
        this.lazyValue = { ...this.lazyValue, rules: [ ...this.lazyValue.rules, newCondition]}
      } else {
        this.lazyValue = {
          rules: [ this.lazyValue, newCondition ],
          id: uniqid(),
          type: 'all', 
        }
      }
    },
    removeGroup (i) {
      this.lazyValue.rules.splice(i, 1)
      this.$nextTick(() => {
        if (this.lazyValue.rules.length === 1) this.lazyValue = this.lazyValue.rules[0]
      })
    }
  }
}
</script>

<style lang="css">
.si-compare-radio .v-input--selection-controls{
  margin-top: 0px !important;
  padding-top: 0px !important;
}

</style>
